import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  ArrowLeft, 
  Save, 
  Image as ImageIcon,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Filter,
  X
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Helper for authenticated fetch
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('session_token');
  const headers = { ...options.headers };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return fetch(url, { ...options, credentials: 'include', headers });
};

// Convert Google Drive URLs to reliable thumbnail URLs
const convertGoogleDriveUrl = (url) => {
  if (!url) return url;
  if (url.includes('drive.google.com/thumbnail') || url.includes('lh3.googleusercontent.com')) return url;
  
  const fileMatch = url.match(/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/thumbnail?id=${fileMatch[1]}&sz=w1000`;
  
  const openMatch = url.match(/drive\.google\.com\/open\?id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/thumbnail?id=${openMatch[1]}&sz=w1000`;
  
  const ucMatch = url.match(/drive\.google\.com\/uc\?(?:export=view&)?id=([a-zA-Z0-9_-]+)/);
  if (ucMatch) return `https://drive.google.com/thumbnail?id=${ucMatch[1]}&sz=w1000`;
  
  return url;
};

// Check if URL is likely broken (old preview URLs)
const isBrokenUrl = (url) => {
  if (!url) return false;
  // Check for old preview environment URLs
  if (url.includes('.preview.emergentagent.com') && !url.includes('contentrev.preview')) return true;
  if (url.includes('artisansphere.preview')) return true;
  if (url.includes('localhost:8001')) return true;
  return false;
};

export const BulkImageUpdaterPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState([]);
  const [artists, setArtists] = useState([]);
  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [editedItems, setEditedItems] = useState({});
  const [filter, setFilter] = useState('all'); // 'all', 'broken', 'content', 'artists', 'projects', 'companies'
  const [imageStatus, setImageStatus] = useState({}); // Track loaded/error status
  const [successMessage, setSuccessMessage] = useState('');

  // Check auth
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await authFetch(`${API_URL}/api/auth/me`);
        if (!response.ok) {
          navigate('/cms/login');
          return;
        }
        const userData = await response.json();
        if (userData.role !== 'admin') {
          navigate('/cms');
          return;
        }
        setUser(userData);
      } catch (error) {
        navigate('/cms/login');
      }
    };
    checkAuth();
  }, [navigate]);

  // Fetch all data
  useEffect(() => {
    if (!user) return;

    const fetchData = async () => {
      try {
        const [contentRes, artistsRes, projectsRes, companiesRes] = await Promise.all([
          authFetch(`${API_URL}/api/content`),
          authFetch(`${API_URL}/api/artists`),
          authFetch(`${API_URL}/api/projects`),
          authFetch(`${API_URL}/api/companies`)
        ]);

        if (contentRes.ok) setContent(await contentRes.json());
        if (artistsRes.ok) setArtists(await artistsRes.json());
        if (projectsRes.ok) setProjects(await projectsRes.json());
        if (companiesRes.ok) setCompanies(await companiesRes.json());
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  // Handle image URL change
  const handleUrlChange = (type, id, field, value) => {
    const key = `${type}-${id}-${field}`;
    setEditedItems(prev => ({
      ...prev,
      [key]: { type, id, field, value }
    }));
  };

  // Get current value (edited or original)
  const getCurrentValue = (type, id, field, originalValue) => {
    const key = `${type}-${id}-${field}`;
    return editedItems[key]?.value ?? originalValue;
  };

  // Check if item has been edited
  const isEdited = (type, id, field) => {
    const key = `${type}-${id}-${field}`;
    return key in editedItems;
  };

  // Save all changes
  const handleSaveAll = async () => {
    const changes = Object.values(editedItems);
    if (changes.length === 0) {
      alert('No changes to save');
      return;
    }

    setSaving(true);
    let successCount = 0;
    let errorCount = 0;

    for (const change of changes) {
      try {
        let url, body;
        
        switch (change.type) {
          case 'content':
            url = `${API_URL}/api/content/${change.id}`;
            body = { [change.field]: convertGoogleDriveUrl(change.value) };
            break;
          case 'artist':
            url = `${API_URL}/api/artists/${change.id}`;
            body = { [change.field]: convertGoogleDriveUrl(change.value) };
            break;
          case 'project':
            url = `${API_URL}/api/projects/${change.id}`;
            body = { [change.field]: convertGoogleDriveUrl(change.value) };
            break;
          case 'company':
            url = `${API_URL}/api/companies/${change.id}`;
            body = { [change.field]: convertGoogleDriveUrl(change.value) };
            break;
          default:
            continue;
        }

        const response = await authFetch(url, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });

        if (response.ok) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        errorCount++;
      }
    }

    setSaving(false);
    setEditedItems({});
    
    // Refresh data
    const [contentRes, artistsRes, projectsRes, companiesRes] = await Promise.all([
      authFetch(`${API_URL}/api/content`),
      authFetch(`${API_URL}/api/artists`),
      authFetch(`${API_URL}/api/projects`),
      authFetch(`${API_URL}/api/companies`)
    ]);

    if (contentRes.ok) setContent(await contentRes.json());
    if (artistsRes.ok) setArtists(await artistsRes.json());
    if (projectsRes.ok) setProjects(await projectsRes.json());
    if (companiesRes.ok) setCompanies(await companiesRes.json());

    setSuccessMessage(`Saved ${successCount} changes${errorCount > 0 ? `, ${errorCount} errors` : ''}`);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Track image load status
  const handleImageLoad = (key) => {
    setImageStatus(prev => ({ ...prev, [key]: 'loaded' }));
  };

  const handleImageError = (key) => {
    setImageStatus(prev => ({ ...prev, [key]: 'error' }));
  };

  // Filter items
  const getFilteredItems = () => {
    let items = [];

    // Add content items
    if (filter === 'all' || filter === 'content' || filter === 'broken') {
      content.forEach(item => {
        if (filter === 'broken' && !isBrokenUrl(item.image) && !isBrokenUrl(item.og_image)) return;
        items.push({
          type: 'content',
          id: item.content_id,
          title: item.title,
          imageField: 'image',
          imageUrl: item.image,
          ogImageField: 'og_image',
          ogImageUrl: item.og_image
        });
      });
    }

    // Add artists
    if (filter === 'all' || filter === 'artists' || filter === 'broken') {
      artists.forEach(item => {
        if (filter === 'broken' && !isBrokenUrl(item.image)) return;
        items.push({
          type: 'artist',
          id: item.artist_id,
          title: item.name,
          imageField: 'image',
          imageUrl: item.image
        });
      });
    }

    // Add projects
    if (filter === 'all' || filter === 'projects' || filter === 'broken') {
      projects.forEach(item => {
        if (filter === 'broken' && !isBrokenUrl(item.image)) return;
        items.push({
          type: 'project',
          id: item.project_id,
          title: item.title,
          imageField: 'image',
          imageUrl: item.image
        });
      });
    }

    // Add companies
    if (filter === 'all' || filter === 'companies' || filter === 'broken') {
      companies.forEach(item => {
        if (filter === 'broken' && !isBrokenUrl(item.logo)) return;
        items.push({
          type: 'company',
          id: item.company_id,
          title: item.name,
          imageField: 'logo',
          imageUrl: item.logo
        });
      });
    }

    return items;
  };

  const pendingChanges = Object.keys(editedItems).length;
  const filteredItems = getFilteredItems();
  const brokenCount = [...content, ...artists, ...projects, ...companies].filter(item => 
    isBrokenUrl(item.image) || isBrokenUrl(item.logo) || isBrokenUrl(item.og_image)
  ).length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="editorial-container py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/cms" className="text-xl editorial-serif">Cultural Revolution</Link>
            <span className="text-xs uppercase tracking-widest text-muted-foreground px-2 py-1 bg-muted">
              Bulk Image Updater
            </span>
          </div>
          <div className="flex items-center gap-3">
            {successMessage && (
              <span className="text-green-500 text-sm flex items-center gap-2">
                <CheckCircle size={16} />
                {successMessage}
              </span>
            )}
            {pendingChanges > 0 && (
              <span className="text-yellow-500 text-sm">
                {pendingChanges} unsaved changes
              </span>
            )}
            <Link to="/cms">
              <Button variant="ghost" size="sm">
                <ArrowLeft size={16} className="mr-2" />
                Back to CMS
              </Button>
            </Link>
            <Button 
              onClick={handleSaveAll} 
              disabled={saving || pendingChanges === 0}
              className={pendingChanges > 0 ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              <Save size={16} className="mr-2" />
              {saving ? 'Saving...' : `Save All (${pendingChanges})`}
            </Button>
          </div>
        </div>
      </header>

      <div className="editorial-container py-8">
        {/* Info Banner */}
        <Card className="p-4 mb-6 bg-yellow-500/10 border-yellow-500/30">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-yellow-500 mt-0.5" size={20} />
            <div>
              <h3 className="font-medium text-yellow-500">Image URL Maintenance</h3>
              <p className="text-sm text-muted-foreground mt-1">
                This tool helps you update broken image URLs. Images hosted on old preview servers will break after deployment.
                Replace them with <strong>Google Drive share links</strong> which are permanent.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                <strong>How to get a Google Drive link:</strong> Upload image to Google Drive → Right-click → Share → 
                "Anyone with the link" → Copy link → Paste here
              </p>
            </div>
          </div>
        </Card>

        {/* Filter Bar */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex items-center gap-2">
            <Filter size={16} className="text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Filter:</span>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[
              { value: 'all', label: 'All Items' },
              { value: 'broken', label: `Broken URLs (${brokenCount})`, highlight: brokenCount > 0 },
              { value: 'content', label: `Content (${content.length})` },
              { value: 'artists', label: `Artists (${artists.length})` },
              { value: 'projects', label: `Projects (${projects.length})` },
              { value: 'companies', label: `Companies (${companies.length})` }
            ].map(f => (
              <button
                key={f.value}
                onClick={() => setFilter(f.value)}
                className={`px-3 py-1.5 text-sm rounded transition-colors ${
                  filter === f.value 
                    ? 'bg-primary text-primary-foreground' 
                    : f.highlight 
                      ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                      : 'bg-muted hover:bg-muted/80'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {/* Items List */}
        <div className="space-y-4">
          {filteredItems.length === 0 ? (
            <Card className="p-8 text-center">
              <ImageIcon size={48} className="mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {filter === 'broken' ? 'No broken image URLs detected!' : 'No items found'}
              </p>
            </Card>
          ) : (
            filteredItems.map((item) => {
              const imageKey = `${item.type}-${item.id}-${item.imageField}`;
              const currentImageUrl = getCurrentValue(item.type, item.id, item.imageField, item.imageUrl);
              const displayUrl = convertGoogleDriveUrl(currentImageUrl);
              const isBroken = isBrokenUrl(item.imageUrl);
              const hasEdits = isEdited(item.type, item.id, item.imageField);

              return (
                <Card key={imageKey} className={`p-4 ${isBroken ? 'border-red-500/50' : ''} ${hasEdits ? 'border-green-500/50' : ''}`}>
                  <div className="flex items-start gap-4">
                    {/* Image Preview */}
                    <div className="w-24 h-24 bg-muted flex-shrink-0 overflow-hidden relative">
                      {currentImageUrl ? (
                        <>
                          <img
                            src={displayUrl}
                            alt={item.title}
                            className="w-full h-full object-cover"
                            onLoad={() => handleImageLoad(imageKey)}
                            onError={() => handleImageError(imageKey)}
                          />
                          {imageStatus[imageKey] === 'error' && (
                            <div className="absolute inset-0 bg-red-500/20 flex items-center justify-center">
                              <X size={24} className="text-red-500" />
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <ImageIcon size={24} className="text-muted-foreground" />
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`text-xs px-2 py-0.5 uppercase tracking-wider ${
                          item.type === 'content' ? 'bg-blue-500/20 text-blue-400' :
                          item.type === 'artist' ? 'bg-purple-500/20 text-purple-400' :
                          item.type === 'project' ? 'bg-green-500/20 text-green-400' :
                          'bg-orange-500/20 text-orange-400'
                        }`}>
                          {item.type}
                        </span>
                        {isBroken && (
                          <span className="text-xs px-2 py-0.5 bg-red-500/20 text-red-400 uppercase tracking-wider flex items-center gap-1">
                            <AlertTriangle size={10} />
                            Broken URL
                          </span>
                        )}
                        {hasEdits && (
                          <span className="text-xs px-2 py-0.5 bg-green-500/20 text-green-400 uppercase tracking-wider">
                            Modified
                          </span>
                        )}
                      </div>
                      
                      <h3 className="font-medium truncate mb-2">{item.title}</h3>
                      
                      <div className="space-y-2">
                        <div>
                          <label className="text-xs text-muted-foreground block mb-1">
                            {item.imageField === 'logo' ? 'Logo URL' : 'Image URL'}
                          </label>
                          <input
                            type="text"
                            value={currentImageUrl || ''}
                            onChange={(e) => handleUrlChange(item.type, item.id, item.imageField, e.target.value)}
                            className={`w-full px-3 py-2 bg-background border rounded text-sm ${
                              isBroken ? 'border-red-500/50' : hasEdits ? 'border-green-500/50' : 'border-border'
                            }`}
                            placeholder="Paste Google Drive share link here..."
                          />
                        </div>

                        {/* OG Image for content */}
                        {item.type === 'content' && item.ogImageUrl !== undefined && (
                          <div>
                            <label className="text-xs text-muted-foreground block mb-1">
                              OG Image URL (for social sharing)
                            </label>
                            <input
                              type="text"
                              value={getCurrentValue(item.type, item.id, item.ogImageField, item.ogImageUrl) || ''}
                              onChange={(e) => handleUrlChange(item.type, item.id, item.ogImageField, e.target.value)}
                              className={`w-full px-3 py-2 bg-background border rounded text-sm ${
                                isBrokenUrl(item.ogImageUrl) ? 'border-red-500/50' : 
                                isEdited(item.type, item.id, item.ogImageField) ? 'border-green-500/50' : 'border-border'
                              }`}
                              placeholder="Paste Google Drive share link here..."
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })
          )}
        </div>

        {/* Floating Save Button */}
        {pendingChanges > 0 && (
          <div className="fixed bottom-6 right-6">
            <Button 
              onClick={handleSaveAll} 
              disabled={saving}
              size="lg"
              className="bg-green-600 hover:bg-green-700 shadow-lg"
            >
              <Save size={20} className="mr-2" />
              {saving ? 'Saving...' : `Save ${pendingChanges} Changes`}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkImageUpdaterPage;
